import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { handleError } from '../tools/handle-error';
import { ApiRespuesta, ApiRespuestaPaginada } from '@app/models/ApiRespuesta.interface';
import { Linea } from '@app/models/linea';
import { Empresa } from '@app/models/empresa';
import { DescripcionUsuario } from '@app/interfaces/general/descripcion-usuario';
import { Nacionalidad } from '@app/interfaces/general/nacionalidad.interface';
import { Genero } from '@app/interfaces/general/genero.interface';
import { AprobadorRrhh } from '@app/shared/interfaces/aprobador-rrhh';
import { Usuario } from '@app/models/usuario';
import { Rol } from '@app/models/rol';

@Injectable({
    providedIn: 'root'
})
export class UsuariosService {
    private url = environment.url + 'usuario/';
    private readonly http = inject(HttpClient);
    private readonly aprobacionUrl = environment.url + 'aprobacion-permiso';

    initForm() {
        const url = this.url + 'auth/init';
        return this.http
            .get<
                ApiRespuesta<{
                    lineas: Linea[];
                    empresas: Empresa[];
                    descripciones: DescripcionUsuario[];
                    nacionalidades: Nacionalidad[];
                    generos: Genero[];
                    roles: Rol[]
                }>
            >(url)
            .pipe(
                catchError((err) => handleError(err)),
                map((data) => data.result)
            );
    }

    register(data: FormData) {
        const url = environment.url + 'usuario/auth/register';
        return this.http.post(url, data).pipe(catchError((err) => handleError(err)));
    }

    getAll(params?: HttpParams) {
        const url = this.url + 'user/get/all';
        return this.http.get<ApiRespuestaPaginada<Usuario>>(url, { params }).pipe(
            catchError((err) => handleError(err)),
            map((data) => data.result)
        );
    }

    getOne(id: string) {
        const url = this.url + id;
        return this.http.get<ApiRespuesta<Usuario>>(url).pipe(
            catchError((err) => handleError(err)),
            map((data) => data.result)
        );
    }

    getAllAprobadores(params?: HttpParams): Observable<ApiRespuestaPaginada<AprobadorRrhh>> {
        const url = this.url + 'aprobadores';
        return this.http.get<ApiRespuestaPaginada<AprobadorRrhh>>(url, { params }).pipe(catchError((err) => handleError(err)));
    }

    update(id: string, data: FormData) {
        const url = this.url + id;

        return this.http.put<ApiRespuesta<Usuario>>(url, data).pipe(
            catchError((err) => handleError(err)),
            map((data) => data.result)
        );
    }

    updateActivo(id: string) {
        const url = this.url + 'updateActivo/' + id;
        return this.http.patch<Usuario>(url, {});
    }

    updateDiasFranco(id: string, dias_franco: number): Observable<ApiRespuesta<any>> {
        const url = this.url + 'updateDiasFranco/' + id;
        return this.http.patch<ApiRespuesta<any>>(url, { rh_dias_franco_disponibles: dias_franco }).pipe(catchError((err) => handleError(err)));
    }

    getOneWithVacaciones(id: string, anio?: number): Observable<ApiRespuesta<any>> {
        let url = this.url + id + '/vacaciones';
        if (anio) {
            url += `?anio=${anio}`;
        }
        return this.http.get<ApiRespuesta<any>>(url).pipe(catchError((err) => handleError(err)));
    }

    updateVacaciones(id: string, vacaciones: Array<{ anio: number; dias: number }>): Observable<ApiRespuesta<any>> {
        const url = this.url + 'updateVacaciones/' + id;
        return this.http.patch<ApiRespuesta<any>>(url, { vacaciones }).pipe(catchError((err) => handleError(err)));
    }

    getSolicitudesCount(id: string): Observable<ApiRespuesta<any>> {
        const url = this.url + id + '/solicitudes/count';
        return this.http.get<ApiRespuesta<any>>(url).pipe(catchError((err) => handleError(err)));
    }

    getSolicitudesDetalladas(id: string): Observable<ApiRespuesta<any>> {
        const url = this.url + id + '/solicitudes';
        return this.http.get<ApiRespuesta<any>>(url).pipe(catchError((err) => handleError(err)));
    }

    // Aprobación: obtener y guardar asignaciones de un usuario
    getUserAprobaciones(usuarioId: string, opts?: { modulo?: string; moduloId?: string; submodulo?: string; submoduloId?: string }) {
        let params = new HttpParams().set('usuarioId', usuarioId);
        if (opts?.modulo) params = params.set('modulo', opts.modulo);
        if (opts?.moduloId) params = params.set('moduloId', opts.moduloId);
        if (opts?.submodulo) params = params.set('submodulo', opts.submodulo);
        if (opts?.submoduloId) params = params.set('submoduloId', opts.submoduloId);
        return this.http.get<ApiRespuestaPaginada<any>>(this.aprobacionUrl + '/user', { params }).pipe(
            catchError((err) => handleError(err)),
            map((res: any) => res.result)
        );
    }

    saveUserAprobaciones(body: { usuarioId: string; moduloId?: string; moduloNombre?: string; submoduloNombre?: string; distritoIds: string[]; tipoPlanillaIds: string[]; usuarioCargaId?: string }) {
        return this.http.post<ApiRespuesta<any>>(this.aprobacionUrl + '/user', body).pipe(
            catchError((err) => handleError(err)),
            map((res: any) => res.result)
        );
    }

    downloadExcel(params: HttpParams) {
        const direction = this.url + 'download-excel';
        return this.http.get(direction, { params, responseType: 'blob' });
    }

    cambiarClave(oldPassword: string, newPassword: string): Observable<any> {
        const url = this.url + 'change-password';
        return this.http.patch<any>(url, { oldPassword, newPassword }).pipe(catchError((err) => handleError(err)));
    }

    updatePerfil(id: string, data: { nombre?: string; apellido?: string }, file?: File): Observable<ApiRespuesta<Usuario>> {
        const url = this.url + id;
        const formData = new FormData();
        formData.append('form', JSON.stringify(data));
        if (file) formData.append('file', file);
        return this.http.put<ApiRespuesta<Usuario>>(url, formData).pipe(
            catchError((err) => handleError(err)),
            map((res) => res)
        );
    }
}

